import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import toastr from "../../utils/toast";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const SECTOR_COLORS = [
  "#f2b90c",
  "#3ac977",
  "#4a7dff",
  "#f24c4c",
  "#a95edb",
  "#20c9d1",
];
const REGION_ID_INDIA = 1;
const REGION_ID_INTERNATIONAL = 2;

// Confirm this env var name matches your setup — falls back to API_BASE_URL if unset.
const PINE_BASE_URL =
  process.env.REACT_APP_PINE_BASE_URL || process.env.REACT_APP_API_BASE_URL;

function profilePicUrl(userpic) {
  if (!userpic) return null;
  return `${PINE_BASE_URL}/files/media/${userpic}`;
}

function StatCard({ label, value, icon, iconClass }) {
  return (
    <div className="col-xl-3 col-md-6 d-flex">
      <div className="card flex-fill">
        <div className="card-body my-card-body">
          <div className="d-flex align-items-center justify-content-between flex-row-reverse">
            <div className="flex-shrink-0 me-2">
              <span className="p-2 br-10 d-flex align-items-center justify-content-center">
                <i className={`${icon} ${iconClass} text-opacity-25 fs-40`}></i>
              </span>
            </div>
            <div>
              <p className="fs-12 fw-medium mb-0 text-gray-5 mb-1">{label}</p>
              <h4 className="counter">{value}</h4>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Custom XAxis tick rendering a circular profile picture instead of text.
function ProfilePicTick({ x, y, payload, picMap }) {
  const pic = picMap[payload.value];
  const clipId = `pic-clip-${payload.value}`;
  if (!pic) {
    return (
      <g transform={`translate(${x},${y})`}>
        <circle cx={0} cy={16} r={14} fill="#e9ecef" />
        <text x={0} y={20} textAnchor="middle" fontSize={11} fill="#888">
          ?
        </text>
      </g>
    );
  }
  return (
    <g transform={`translate(${x},${y})`}>
      <clipPath id={clipId}>
        <circle cx={0} cy={16} r={14} />
      </clipPath>
      <image
        href={pic}
        x={-14}
        y={2}
        width={28}
        height={28}
        clipPath={`url(#${clipId})`}
        preserveAspectRatio="xMidYMid slice"
      />
      <circle
        cx={0}
        cy={16}
        r={14}
        fill="none"
        stroke="#dcdfe5"
        strokeWidth={1}
      />
    </g>
  );
}

function AdminView() {
  const navigate = useNavigate();
  const { setGlobalLoading, token } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [selectedPartnerId, setSelectedPartnerId] = useState(null); // null = all partners
  const [data, setData] = useState(null);

  const fetchAdminOverview = async () => {
    try {
      setGlobalLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/companyinfo/admin_overview`,
        {
          params: selectedPartnerId ? { partner_id: selectedPartnerId } : {},
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.data) {
        setData(res.data);
      }
    } catch (error) {
      toastr.error(
        error?.response?.data?.message || "Failed to load admin overview",
      );
    } finally {
      setGlobalLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPartnerId]);

  if (!data) {
    return (
      <div className="content">
        <h2 className="mb-1">Admin Oversight</h2>
      </div>
    );
  }

  const {
    all_partners = [],
    active_partners,
    total_prospects,
    contacts_added_total,
    stale_total,
    partner_scorecard = [],
    sector_coverage = [],
    geo_summary = [],
    geo_table = [],
    load_activity = [],
    activity_feed = [],
    stage_coverage = [],
    partner_names = [],
    needs_attention = [],
  } = data;

  const sectorTotal = sector_coverage.reduce((s, x) => s + x.prospects, 0) || 1;
  const indiaCount =
    geo_summary.find((g) => g.bd_region_id === REGION_ID_INDIA)?.total || 0;
  const intlCount =
    geo_summary.find((g) => g.bd_region_id === REGION_ID_INTERNATIONAL)
      ?.total || 0;

  const picMap = {};
  load_activity.forEach((r) => {
    picMap[r.partner_id] = profilePicUrl(r.userpic);
  });

  const selectedPartnerLabel = selectedPartnerId
    ? all_partners.find((p) => p.id === selectedPartnerId)?.name || "Partner"
    : "All Partners";

  return (
    <>
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Admin Oversight</h2>
            <p className="text-muted mb-0">
              Activity across all partner BD pipelines
            </p>
          </div>
          <div className="my-auto mb-2 d-flex align-items-center">
            <div className="dropdown me-2">
              <button
                className="btn btn-outline-secondary rounded-pill dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {selectedPartnerLabel}
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => setSelectedPartnerId(null)}
                  >
                    All Partners
                  </button>
                </li>
                {all_partners.map((p) => (
                  <li key={p.id}>
                    <button
                      className="dropdown-item"
                      onClick={() => setSelectedPartnerId(p.id)}
                    >
                      {p.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <button
              onClick={() =>
                navigate("/lead_dashboard/pipeline")
              }
              className="btn btn-outline-secondary rounded-pill"
            >
              View BD Pipeline
              <i className="fa-solid fa-arrow-right ms-2"></i>
            </button>
          </div>
        </div>

        {/* Top stat cards */}
        <div className="row">
          <StatCard
            label="Active Partners"
            value={active_partners}
            icon="fa-solid fa-users"
            iconClass="text-info"
          />
          <StatCard
            label="Total Prospects Mapped"
            value={total_prospects}
            icon="fa-solid fa-building"
            iconClass="text-primary"
          />
          <StatCard
            label="Contacts Added"
            value={contacts_added_total}
            icon="fa-solid fa-user-plus"
            iconClass="text-success"
          />
          <StatCard
            label="Stale (3M+)"
            value={stale_total}
            icon="fa-solid fa-triangle-exclamation"
            iconClass="text-danger"
          />
        </div>

        {/* Partner Scorecard */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <h3 className="card-title">Partner Scorecard</h3>
                <div className="table-responsive">
                  <table className="table table-borderless mb-0">
                    <thead>
                      <tr className="text-gray-5 fs-13">
                        <th>Partner Name</th>
                        <th>Prospects Added (FY)</th>
                        <th>New BD Added (FY)</th>
                        <th>Contacts Added</th>
                        <th>Contacts Met</th>
                        <th>Last Meeting Date</th>
                        <th>Stale Prospects (3M+)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partner_scorecard.map((p) => (
                        <tr key={p.id}>
                          <td className="fw-medium">{p.name}</td>
                          <td>{p.prospectsAdded}</td>
                          <td>{p.newBdAdded}</td>
                          <td>{p.contactsAdded}</td>
                          <td>{p.contactsMet}</td>
                          <td>
                            {p.lastMeetingDate
                              ? p.lastMeetingDate.slice(0, 10)
                              : "—"}
                          </td>
                          <td>{p.staleProspects}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sector-wise + Geography-wise coverage */}
        <div className="row">
          <div className="col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h3 className="card-title">
                  <i className="fa-solid fa-layer-group me-1"></i>Sector-wise
                  Coverage
                </h3>
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={sector_coverage}
                        dataKey="prospects"
                        nameKey="industry_name"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                      >
                        {sector_coverage.map((entry, i) => (
                          <Cell
                            key={entry.industry_id}
                            fill={SECTOR_COLORS[i % SECTOR_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2">
                  {sector_coverage.map((s, i) => (
                    <div key={s.industry_id} className="mb-3">
                      <div className="d-flex justify-content-between fs-13">
                        <span className="fw-medium">{s.industry_name}</span>
                        <span className="text-muted">
                          {s.prospects} prospects · {s.partners} partner
                          {s.partners !== 1 ? "s" : ""} · {s.late_stage} late
                          stage
                        </span>
                      </div>
                      <div className="progress" style={{ height: 6 }}>
                        <div
                          className="progress-bar"
                          style={{
                            width: `${(s.prospects / sectorTotal) * 100}%`,
                            backgroundColor:
                              SECTOR_COLORS[i % SECTOR_COLORS.length],
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h3 className="card-title">
                  <i className="fa-solid fa-globe me-1"></i>Geography-wise
                  Coverage
                </h3>
                <div className="row mb-3">
                  <div className="col-6">
                    <div className="border rounded-3 p-3">
                      <p className="fs-12 text-gray-5 mb-1">INDIA</p>
                      <h4 className="mb-0">{indiaCount}</h4>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="border rounded-3 p-3">
                      <p className="fs-12 text-gray-5 mb-1">INTERNATIONAL</p>
                      <h4 className="mb-0">{intlCount}</h4>
                    </div>
                  </div>
                </div>
                <div style={{ width: "100%", height: 200 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={geo_table}
                        dataKey="prospects"
                        nameKey="region_name"
                        innerRadius={55}
                        outerRadius={85}
                        paddingAngle={2}
                      >
                        {geo_table.map((entry, i) => (
                          <Cell
                            key={entry.region_id}
                            fill={SECTOR_COLORS[i % SECTOR_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="table-responsive mt-2">
                  <table className="table table-borderless mb-0 fs-13">
                    <thead>
                      <tr className="text-gray-5">
                        <th>Continental Region</th>
                        <th>Locations</th>
                        <th>Prospects</th>
                        <th>Partners</th>
                      </tr>
                    </thead>
                    <tbody>
                      {geo_table.map((r) => (
                        <tr key={r.region_id}>
                          <td className="fw-medium">{r.region_name}</td>
                          <td>{r.locations}</td>
                          <td>{r.prospects}</td>
                          <td>{r.partners}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Load & Activity + Activity Feed */}
        <div className="row">
          <div className="col-md-7 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h3 className="card-title">Load &amp; Activity by Partner</h3>
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer>
                    <BarChart data={load_activity} margin={{ bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="partner_id"
                        height={40}
                        tick={(props) => (
                          <ProfilePicTick {...props} picMap={picMap} />
                        )}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip
                        labelFormatter={(partnerId) =>
                          load_activity.find((r) => r.partner_id === partnerId)
                            ?.partner_name || ""
                        }
                      />
                      <Legend />
                      <Bar dataKey="Prospects" fill="#f2b90c" />
                      <Bar dataKey="LateStage" fill="#f24c4c" />
                      <Bar dataKey="Activities" fill="#3ac977" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-5 d-flex">
            <div className="card flex-fill" style={{ maxHeight: "22pc" }}>
              <div className="card-body" style={{ overflowY: "auto" }}>
                <h3 className="card-title">
                  <i className="fa-solid fa-bolt text-warning me-1"></i>Activity
                  Feed
                </h3>
                {activity_feed.length === 0 && (
                  <p className="text-muted fs-13">No recent activity.</p>
                )}
                {activity_feed.map((a, idx) => (
                  <div
                    key={idx}
                    className="d-flex justify-content-between border-bottom py-2"
                  >
                    <div>
                      <div className="fw-medium">{a.partner_name || "—"}</div>
                      <div className="fs-13">
                        {a.notes || `Meeting ${a.status}`}
                      </div>
                      <div className="text-muted fs-12">
                        {a.company_name}
                        {a.contact_name ? ` · with ${a.contact_name}` : ""}
                      </div>
                    </div>
                    <div className="text-muted fs-12 text-nowrap ms-2">
                      {a.meeting_date ? a.meeting_date.slice(0, 10) : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Stage Coverage by Partner + Needs Attention */}
        <div className="row">
          <div className="col-md-7 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h3 className="card-title">Stage Coverage by Partner</h3>
                <div className="table-responsive">
                  <table className="table table-borderless mb-0 fs-13">
                    <thead>
                      <tr className="text-gray-5">
                        <th>Stage</th>
                        {partner_names.map((n) => (
                          <th key={n}>{n}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stage_coverage.map((row) => (
                        <tr key={row.stage_id}>
                          <td>
                            <span className="badge bg-light text-dark rounded-pill px-3 py-1">
                              {row.stage}
                            </span>
                          </td>
                          {partner_names.map((n) => (
                            <td key={n}>{row[n] || 0}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-5 d-flex">
            <div className="card flex-fill">
              <div className="card-body">
                <h3 className="card-title">
                  <i className="fa-solid fa-triangle-exclamation text-warning me-1"></i>
                  Needs Attention
                </h3>
                {needs_attention.length === 0 && (
                  <p className="text-muted fs-13">
                    Nothing needs attention right now.
                  </p>
                )}
                {needs_attention.map((n, idx) => (
                  <div
                    key={idx}
                    className="d-flex justify-content-between align-items-center border-bottom py-2"
                  >
                    <div>
                      <div className="fw-medium">{n.company}</div>
                      <div className="text-muted fs-12">
                        {n.partner} · {n.reason}
                      </div>
                    </div>
                    <span className="badge bg-warning-transparent text-warning rounded-pill px-3 py-1 text-nowrap">
                      <i className="fa-regular fa-clock me-1"></i>
                      {n.idleDays}d idle
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AdminView;
